import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Skeleton,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  KeyboardArrowLeft as ArrowLeftIcon,
  KeyboardArrowRight as ArrowRightIcon,
  ArrowOutward as ArrowOutwardIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import API from '../api/axios';
import { getImageUrl, FALLBACK_IMAGE } from '../utils/imageUtils';
import useCategories from '../hooks/useCategories';
import ExploreNow from './ExploreNow';

// ============ Styled Components ============

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  color: theme.palette.text.primary,
  fontFamily: 'sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.4rem',
  },
}));

const TabContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  borderRadius: '50px',
  padding: '4px',
  [theme.breakpoints.down('sm')]: {
    overflowX: 'auto',
    flexWrap: 'nowrap',
    width: '100%',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
}));

const TabButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  borderRadius: '50px',
  padding: '8px 24px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  [theme.breakpoints.down('sm')]: {
    padding: '6px 14px',
    fontSize: '0.8rem',
    flexShrink: 0,
  },
  color: active ? '#fff' : theme.palette.text.secondary,
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  boxShadow: active ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.08),
  },
}));

const CarouselWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  // Add padding to create space for navigation buttons
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '40px',
    paddingRight: '40px',
  },
  [theme.breakpoints.up('sm')]: {
    paddingLeft: '50px',
    paddingRight: '50px',
  },
}));

const CardsContainer = styled(Box)({
  display: 'flex',
  gap: '24px',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  padding: '10px 0',
  width: '100%',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
});

const OfferCard = styled(Box)(({ theme }) => ({
  minWidth: '300px',
  width: '300px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '250px',  // Reduced from 260px for better mobile display
    width: '250px',      // Reduced from 260px for better mobile display
  },
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  flexShrink: 0,
  backgroundColor: '#fff',
  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.12)}`,
  },
}));

const CardImageWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '200px',
  overflow: 'hidden',
});

const CardImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease',
  '&:hover': {
    transform: 'scale(1.08)',
  },
});

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',  // Slightly smaller for mobile
  },
  color: theme.palette.text.primary,
  lineHeight: 1.3,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  minHeight: '2.6rem',
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  width: '44px',
  height: '44px',
  position: 'absolute',
  zIndex: 3,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  [theme.breakpoints.down('sm')]: {
    width: '36px',
    height: '36px',
  },
}));

const ExclusiveOffers = () => {
  const [activeTab, setActiveTab] = useState('');
  const [offersMap, setOffersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const theme = useTheme();

  const { categories: TABS } = useCategories();

  useEffect(() => {
    if (TABS.length > 0 && !activeTab) {
      setActiveTab(TABS[0]);
    }
  }, [TABS, activeTab]);

  useEffect(() => {
    fetchAllOffers();
  }, []);

  const fetchAllOffers = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/offers?active=1');
      const map = {};
      (Array.isArray(data) ? data : []).forEach(offer => {
        if (!map[offer.category]) map[offer.category] = [];
        map[offer.category].push(offer);
      });
      setOffersMap(map);
    } catch {
      setError('Failed to load exclusive offers.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (category) => {
    setActiveTab(category);
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = window.innerWidth > 600 ? 340 : 260;  // Adjusted from 280 to 260 to match new card size
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const currentOffers = offersMap[activeTab] || [];
  const displayedOffers = currentOffers.slice(0, 6);

  return (
    <Box sx={{ bgcolor: '#ffffff', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="xl">
        {/* Header Title & Tabs */}
        <HeaderContainer>
          <Title>Exclusive Holiday Offers</Title>
          <TabContainer>
            {TABS.map(tab => (
              <TabButton
                key={tab}
                active={activeTab === tab}
                onClick={() => handleTabChange(tab)}
                disableElevation
              >
                {tab}
              </TabButton>
            ))}
          </TabContainer>
        </HeaderContainer>

        {/* Content */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="340px">
            <CircularProgress size={48} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 4, borderRadius: 2 }}>{error}</Alert>
        ) : displayedOffers.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              No offers available in this category yet.
            </Typography>
          </Box>
        ) : (
          <CarouselWrapper>
            {/* Left Scroll Button */}
            <NavButton
              onClick={() => scroll('left')}
              sx={{ left: { xs: 0, sm: 0 }, zIndex: 5 }}
              aria-label="Scroll Left"
            >
              <ArrowLeftIcon />
            </NavButton>

            {/* Offer Cards */}
            <CardsContainer ref={scrollRef}>
              {displayedOffers.map((offer) => (
                <OfferCard
                  key={offer.id}
                  onClick={() => navigate(`/exclusive-offers?tab=${activeTab}`)}
                >
                  {/* Image Section */}
                  <CardImageWrapper>
                    <CardImage
                      src={getImageUrl(offer.image_url) || FALLBACK_IMAGE}
                      alt={offer.description}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_IMAGE;
                      }}
                    />
                    {/* Gradient Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)',
                        zIndex: 1,
                      }}
                    />
                    {/* Exclusive Badge */}
                    <Chip
                      icon={<LocalOfferIcon sx={{ fontSize: 14, color: '#ff4757' }} />}
                      label="Exclusive"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        zIndex: 2,
                        bgcolor: 'rgba(255,255,255,0.95)',
                        color: '#333',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        borderRadius: '8px',
                        backdropFilter: 'blur(4px)',
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '0.7rem',
                          padding: '0 6px',
                        },
                      }}
                    />
                  </CardImageWrapper>

                  {/* Content Section */}
                  <CardContent>
                    <CardTitle>{offer.description}</CardTitle>
                  </CardContent>
                </OfferCard>
              ))}
            </CardsContainer>

            {/* Right Scroll Button */}
            <NavButton
              onClick={() => scroll('right')}
              sx={{ right: { xs: 0, sm: 0 }, zIndex: 5 }}
              aria-label="Scroll Right"
            >
              <ArrowRightIcon />
            </NavButton>
          </CarouselWrapper>
        )}

        {/* Explore Now Button */}
        {!loading && displayedOffers.length > 0 && (
          <ExploreNow onClick={() => navigate(`/exclusive-offers?tab=${activeTab}`)} />
        )}
      </Container>
    </Box>
  );
};

export default ExclusiveOffers;